/**
 * AutoLISP lexer — tokens for the S-expression reader. Handles line
 * comments (`;` to end of line), block comments (`;| ... |;`), strings with
 * AutoLISP escapes (\n \t \r \e \" \\ and \nnn octal), numbers and symbols.
 * Symbol names are uppercased (AutoLISP symbols are case-insensitive).
 */

import { LispError } from './errors';

export type Token =
    | { t: 'open' }
    | { t: 'close' }
    | { t: 'quote' }
    | { t: 'dot' }
    | { t: 'str'; v: string }
    | { t: 'num'; v: number; isInt: boolean }
    | { t: 'sym'; v: string };

const NUM_RE = /^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/;
const INT_RE = /^[-+]?\d+$/;

function readString(src: string, start: number): { value: string; end: number } {
    let out = '';
    let i = start + 1; // skip opening quote
    while (i < src.length) {
        const ch = src[i];
        if (ch === '"') return { value: out, end: i + 1 };
        if (ch === '\\') {
            const esc = src[i + 1];
            i += 2;
            switch (esc) {
                case 'n':
                    out += '\n';
                    break;
                case 't':
                    out += '\t';
                    break;
                case 'r':
                    out += '\r';
                    break;
                case 'e':
                    out += '\u001b';
                    break;
                case '"':
                    out += '"';
                    break;
                case '\\':
                    out += '\\';
                    break;
                default:
                    // \nnn octal escape (1-3 digits)
                    if (esc && esc >= '0' && esc <= '7') {
                        let oct = esc;
                        while (oct.length < 3 && src[i] >= '0' && src[i] <= '7') oct += src[i++];
                        out += String.fromCharCode(parseInt(oct, 8));
                    } else {
                        out += esc ?? '';
                    }
            }
            continue;
        }
        out += ch;
        i++;
    }
    throw new LispError('malformed string');
}

export function tokenize(src: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    while (i < src.length) {
        const ch = src[i];
        if (/\s/.test(ch)) {
            i++;
            continue;
        }
        if (ch === ';') {
            // ";" line comment, ";|" block comment
            if (src[i + 1] === '|') {
                const end = src.indexOf('|;', i + 2);
                if (end === -1) throw new LispError('unterminated block comment');
                i = end + 2;
            } else {
                while (i < src.length && src[i] !== '\n') i++;
            }
            continue;
        }
        if (ch === '(') {
            tokens.push({ t: 'open' });
            i++;
            continue;
        }
        if (ch === ')') {
            tokens.push({ t: 'close' });
            i++;
            continue;
        }
        if (ch === "'") {
            tokens.push({ t: 'quote' });
            i++;
            continue;
        }
        if (ch === '"') {
            const { value, end } = readString(src, i);
            tokens.push({ t: 'str', v: value });
            i = end;
            continue;
        }
        // Atom: read until whitespace or delimiter.
        let j = i;
        while (j < src.length && !/[\s()';"]/.test(src[j])) j++;
        const atom = src.slice(i, j);
        i = j;
        if (atom === '.') {
            tokens.push({ t: 'dot' });
        } else if (NUM_RE.test(atom)) {
            const v = Number(atom);
            tokens.push({ t: 'num', v, isInt: INT_RE.test(atom) });
        } else {
            tokens.push({ t: 'sym', v: atom.toUpperCase() });
        }
    }
    return tokens;
}
